// Datoer og tidsrum på dansk, og danske helligdage. Datoer er tekst "YYYY-MM-DD"; tider er minutter efter midnat.

/** Datoen n dage senere (eller tidligere med negativt n). */
export function plusDage(dato: string, n: number): string {
  const [y, m, d] = dato.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
const dagIUgen = (dato: string) => { const [y, m, d] = dato.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };
// Ugedag – tre nummereringer findes i systemet, så navnet siger, hvilken der menes:
/** 1 = mandag … 7 = søndag (arbejdstider, fast fri og Postgres' isodow). */
export const ugedagMandag1 = (dato: string): number => dagIUgen(dato) || 7;
/** 0 = mandag … 6 = søndag (kalendergitre, der starter mandag). */
export const ugedagMandag0 = (dato: string): number => (dagIUgen(dato) + 6) % 7;
/** 0 = søndag … 6 = lørdag (JavaScripts getDay og Postgres' dow). */
export const ugedagSoendag0 = (dato: string): number => dagIUgen(dato);
/** Minutter efter midnat → "HH:MM" (ingen tid → ''). */
export const klokken = (min: number | null | undefined): string => (min == null ? '' : String(Math.floor(min / 60)).padStart(2, '0') + ':' + String(min % 60).padStart(2, '0'));

const MAANEDER = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];
const DAGE = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];

export function datoTekst(dato: string, medDag = true, aar?: number): string {
  const [y, m, d] = dato.split('-').map(Number);
  const w = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return (medDag ? DAGE[w] + ' ' : '') + d + '. ' + MAANEDER[m - 1] + (y !== (aar ?? new Date().getFullYear()) ? ' ' + y : '');
}
export const maanedNavn = (m: number) => MAANEDER[m];
export const dagNavn = (w: number) => DAGE[w];

export function tidsrum(start: number | null, slut: number | null): string {
  return start == null || slut == null ? 'tid aftales' : `kl. ${klokken(start)}–${klokken(slut)}`;
}

export function forNavn(navn: string) { return navn.trim().split(/\s+/)[0] || navn; }

/** Påskedag (gregoriansk, anonym algoritme). */
function paaske(aar: number): string {
  const a = aar % 19, b = Math.floor(aar / 100), c = aar % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const md = Math.floor((h + l - 7 * m + 114) / 31), dag = ((h + l - 7 * m + 114) % 31) + 1;
  return `${aar}-${String(md).padStart(2, '0')}-${String(dag).padStart(2, '0')}`;
}

/** Danske helligdage (store bededag er afskaffet fra 2024). */
export function helligdage(aar: number): { dato: string; navn: string }[] {
  const p = paaske(aar);
  return [
    { dato: `${aar}-01-01`, navn: 'Nytårsdag' },
    { dato: plusDage(p, -3), navn: 'Skærtorsdag' },
    { dato: plusDage(p, -2), navn: 'Langfredag' },
    { dato: p, navn: 'Påskedag' },
    { dato: plusDage(p, 1), navn: '2. påskedag' },
    { dato: plusDage(p, 39), navn: 'Kristi himmelfartsdag' },
    { dato: plusDage(p, 49), navn: 'Pinsedag' },
    { dato: plusDage(p, 50), navn: '2. pinsedag' },
    { dato: `${aar}-06-05`, navn: 'Grundlovsdag' },
    { dato: `${aar}-12-24`, navn: 'Juleaftensdag' },
    { dato: `${aar}-12-25`, navn: '1. juledag' },
    { dato: `${aar}-12-26`, navn: '2. juledag' },
    { dato: `${aar}-12-31`, navn: 'Nytårsaftensdag' },
  ];
}

/** Dagens dato i dansk tid, "YYYY-MM-DD". */
export function iDag(d = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Copenhagen', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}
/** Minutter efter midnat lige nu i dansk tid. */
export function lokalMin(d = new Date()): number {
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Copenhagen', hour: '2-digit', minute: '2-digit', hour12: false }).format(d).split(':').map(Number);
  return (p[0] % 24) * 60 + p[1];
}
const KBH = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Copenhagen', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
/** Danmarks forskydning fra UTC i minutter på tidspunktet (60 om vinteren, 120 om sommeren). */
function kbhForskydning(ms: number): number {
  const p = Object.fromEntries(KBH.formatToParts(new Date(ms)).map((x) => [x.type, Number(x.value)]));
  return Math.round((Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(ms / 6e4) * 6e4) / 6e4);
}
/** Tidspunktet (ms) for dato + minutter efter midnat i dansk tid – med sommer-/vintertid (som `timestamp at time zone 'Europe/Copenhagen'`). */
export function danskTidspunkt(dato: string, min: number): number {
  const lokal = Date.parse(dato + 'T00:00:00Z') + min * 6e4;
  return lokal - kbhForskydning(lokal - kbhForskydning(lokal) * 6e4) * 6e4;
}
/** Minutter fra nu (hele minutter) til et tidspunkt på en dato i dansk tid – rigtigt hen over skift til/fra sommertid.
 *  Samme regel som pixie_minutter_til i databasen (20261031100000_ret_booking_pris.sql). Negativ = tiden er passeret. */
export function minutterTil(dato: string, min: number, nu = new Date()): number {
  return Math.round((danskTidspunkt(dato, min) - Math.floor(nu.getTime() / 6e4) * 6e4) / 6e4);
}
